import { useState, useEffect, useMemo } from "react"
import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceCenter,
  forceCollide,
  type SimulationNodeDatum,
  type SimulationLinkDatum,
} from "d3-force"
import { usePlayback } from "./usePlayback"
import { PlaybackControls } from "./PlaybackControls"
import type { GraphNode, GraphEdge, GraphStep } from "./types"

interface SimNode extends SimulationNodeDatum {
  id: string | number
  label?: string
}

interface SimLink extends SimulationLinkDatum<SimNode> {
  source: number
  target: number
}

interface ExtraArray {
  label: string
  values: (number | string)[]
}

interface GraphVizProps {
  nodes: GraphNode[]
  edges: GraphEdge[]
  steps: GraphStep[]
  extraArrays?: ExtraArray[]
  width?: number
  height?: number
  directed?: boolean
}

interface PositionedNode {
  id: string | number
  label?: string
  x: number
  y: number
}

function edgeKey(source: string | number, target: string | number): string {
  return `${source}-${target}`
}

function computeForceLayout(
  nodes: GraphNode[],
  edges: GraphEdge[],
  width: number,
  height: number
): PositionedNode[] {
  const simNodes: SimNode[] = nodes.map((n) => ({
    id: n.id,
    label: n.label,
    x: Math.random() * width,
    y: Math.random() * height,
  }))

  const idIndex = new Map(simNodes.map((n, i) => [n.id, i]))

  const simLinks: SimLink[] = edges
    .filter(
      (e) => idIndex.has(e.source) && idIndex.has(e.target)
    )
    .map((e) => ({
      source: idIndex.get(e.source)!,
      target: idIndex.get(e.target)!,
    }))

  const simulation = forceSimulation<SimNode>(simNodes)
    .force(
      "link",
      forceLink<SimNode, SimLink>(simLinks).distance(100)
    )
    .force("charge", forceManyBody().strength(-300))
    .force("center", forceCenter(width / 2, height / 2))
    .force("collide", forceCollide(30))
    .stop()

  // Run synchronously for a stable layout
  for (let i = 0; i < 300; i++) {
    simulation.tick()
  }

  // Scale positions to fit within bounds
  const innerW = width - 80
  const innerH = height - 80

  let minX = Infinity, maxX = -Infinity
  let minY = Infinity, maxY = -Infinity
  for (const n of simNodes) {
    const nx = n.x ?? 0
    const ny = n.y ?? 0
    if (nx < minX) minX = nx
    if (nx > maxX) maxX = nx
    if (ny < minY) minY = ny
    if (ny > maxY) maxY = ny
  }

  const rangeX = maxX - minX || 1
  const rangeY = maxY - minY || 1

  return simNodes.map((n) => ({
    id: n.id,
    label: n.label,
    x: (((n.x ?? 0) - minX) / rangeX) * innerW,
    y: (((n.y ?? 0) - minY) / rangeY) * innerH,
  }))
}

const CELL_SIZE = 40
const CELL_GAP = 6

export function GraphViz({
  nodes,
  edges,
  steps,
  extraArrays = [],
  width = 600,
  height: heightProp = 400,
  directed = false,
}: GraphVizProps) {
  const extraRowHeight = extraArrays.length > 0 ? 70 : 0
  const height = heightProp + extraRowHeight
  const { stepIndex, isPlaying, atStart, atEnd, stepForward, stepBack, play, pause, reset } =
    usePlayback(steps.length)

  // Compute node positions
  const [positions, setPositions] = useState<PositionedNode[]>([])

  const hasFixedPositions = useMemo(
    () => nodes.every((n) => n.x !== undefined && n.y !== undefined),
    [nodes]
  )

  useEffect(() => {
    if (hasFixedPositions) {
      setPositions(
        nodes.map((n) => ({
          id: n.id,
          label: n.label,
          x: n.x!,
          y: n.y!,
        }))
      )
    } else {
      const computed = computeForceLayout(nodes, edges, width - 80, height - 80)
      setPositions(computed)
    }
  }, [nodes, edges, width, height, hasFixedPositions])

  const posMap = useMemo(() => {
    const m = new Map<string | number, PositionedNode>()
    for (const p of positions) {
      m.set(p.id, p)
    }
    return m
  }, [positions])

  // Replay steps 0..stepIndex to compute visual state
  const visualState = useMemo(() => {
    const nodeColors = new Map<string | number, string>()
    const edgeColors = new Map<string, string>()
    const visitedNodes = new Set<string | number>()
    const edgeWeightOverrides = new Map<string, string | number>()
    let highlightedPath: (string | number)[] = []

    for (let i = 0; i <= stepIndex; i++) {
      const step = steps[i]
      if (!step) continue

      switch (step.type) {
        case "visit":
          if (step.nodeId !== undefined) {
            nodeColors.set(step.nodeId, step.color ?? "#6366f1")
          }
          break

        case "explore_edge":
          if (step.edgeSource !== undefined && step.edgeTarget !== undefined) {
            const key = edgeKey(step.edgeSource, step.edgeTarget)
            const reverseKey = edgeKey(step.edgeTarget, step.edgeSource)
            const color = step.color ?? "#f59e0b"
            edgeColors.set(key, color)
            if (!directed) {
              edgeColors.set(reverseKey, color)
            }
          }
          break

        case "relax":
          if (step.edgeSource !== undefined && step.edgeTarget !== undefined) {
            const key = edgeKey(step.edgeSource, step.edgeTarget)
            const reverseKey = edgeKey(step.edgeTarget, step.edgeSource)
            edgeColors.set(key, step.color ?? "#22c55e")
            if (!directed) {
              edgeColors.set(reverseKey, step.color ?? "#22c55e")
            }
          }
          break

        case "highlight_path":
          if (step.path && step.path.length > 0) {
            highlightedPath = step.path
            const pathColor = step.color ?? "#6366f1"
            for (const nid of step.path) {
              nodeColors.set(nid, pathColor)
            }
            for (let j = 0; j < step.path.length - 1; j++) {
              const key = edgeKey(step.path[j]!, step.path[j + 1]!)
              const reverseKey = edgeKey(step.path[j + 1]!, step.path[j]!)
              edgeColors.set(key, pathColor)
              if (!directed) {
                edgeColors.set(reverseKey, pathColor)
              }
            }
          }
          break

        case "mark_visited":
          if (step.nodeId !== undefined) {
            visitedNodes.add(step.nodeId)
            nodeColors.set(step.nodeId, step.color ?? "#94a3b8")
          }
          break

        case "update_weight":
          if (step.edgeSource !== undefined && step.edgeTarget !== undefined && step.value !== undefined) {
            const key = edgeKey(step.edgeSource, step.edgeTarget)
            edgeWeightOverrides.set(key, step.value)
          }
          break

        case "reset":
          nodeColors.clear()
          edgeColors.clear()
          visitedNodes.clear()
          edgeWeightOverrides.clear()
          highlightedPath = []
          break
      }
    }

    return { nodeColors, edgeColors, visitedNodes, edgeWeightOverrides, highlightedPath }
  }, [stepIndex, steps, directed])

  const currentMessage = stepIndex >= 0 && stepIndex < steps.length ? steps[stepIndex]!.message : ""

  const isDirectedEdge = (edge: GraphEdge) => edge.directed ?? directed

  return (
    <div className="flex flex-col gap-3">
      <svg width={width} height={height} className="rounded-lg border border-border bg-background">
        <defs>
          <marker
            id="arrowhead"
            viewBox="0 0 10 7"
            refX="28"
            refY="3.5"
            markerWidth="8"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill="#94a3b8" />
          </marker>
          <marker
            id="arrowhead-colored"
            viewBox="0 0 10 7"
            refX="28"
            refY="3.5"
            markerWidth="8"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill="currentColor" />
          </marker>
        </defs>
        <g transform="translate(40, 40)">
          {/* Edges */}
          {edges.map((edge) => {
            const src = posMap.get(edge.source)
            const tgt = posMap.get(edge.target)
            if (!src || !tgt) return null

            const key = edgeKey(edge.source, edge.target)
            const reverseKey = edgeKey(edge.target, edge.source)
            const color = visualState.edgeColors.get(key) ?? visualState.edgeColors.get(reverseKey) ?? "#d4d4d8"
            const showArrow = isDirectedEdge(edge)

            return (
              <line
                key={key}
                x1={src.x}
                y1={src.y}
                x2={tgt.x}
                y2={tgt.y}
                stroke={color}
                strokeWidth={2}
                markerEnd={showArrow ? "url(#arrowhead)" : undefined}
                style={{ transition: "all 400ms ease" }}
              />
            )
          })}

          {/* Edge weight labels */}
          {edges.map((edge) => {
            const src = posMap.get(edge.source)
            const tgt = posMap.get(edge.target)
            if (!src || !tgt) return null

            const key = edgeKey(edge.source, edge.target)
            const weightOverride = visualState.edgeWeightOverrides.get(key)
            const displayWeight = weightOverride ?? edge.weight

            if (displayWeight === undefined) return null

            const mx = (src.x + tgt.x) / 2
            const my = (src.y + tgt.y) / 2

            return (
              <text
                key={`weight-${key}`}
                x={mx}
                y={my - 8}
                textAnchor="middle"
                className="text-xs fill-muted-foreground"
                style={{ transition: "all 400ms ease" }}
              >
                {displayWeight}
              </text>
            )
          })}

          {/* Nodes */}
          {positions.map((node) => {
            const fill = visualState.nodeColors.get(node.id) ?? "#ffffff"
            const isVisited = visualState.visitedNodes.has(node.id)
            const strokeColor = visualState.nodeColors.has(node.id)
              ? visualState.nodeColors.get(node.id)!
              : "#d4d4d8"

            return (
              <g key={String(node.id)} style={{ transition: "all 400ms ease" }}>
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={22}
                  fill={fill}
                  stroke={strokeColor}
                  strokeWidth={2}
                  style={{ transition: "all 400ms ease" }}
                />
                <text
                  x={node.x}
                  y={node.y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  className="text-sm font-medium select-none pointer-events-none"
                  fill={fill === "#ffffff" ? "#1e293b" : "#ffffff"}
                  style={{ transition: "all 400ms ease" }}
                >
                  {node.label ?? node.id}
                </text>
                {isVisited && !visualState.highlightedPath.includes(node.id) && (
                  <text
                    x={node.x + 14}
                    y={node.y - 14}
                    textAnchor="middle"
                    dominantBaseline="central"
                    className="text-xs font-bold select-none pointer-events-none"
                    fill="#64748b"
                  >
                    &#10003;
                  </text>
                )}
              </g>
            )
          })}
        </g>

        {/* Extra arrays (e.g., distance table, queue, sorted edges) */}
        {extraArrays.map((extra, rowIdx) => {
          const cellW = CELL_SIZE + CELL_GAP
          const totalW = extra.values.length * CELL_SIZE + (extra.values.length - 1) * CELL_GAP
          const ox = (width - totalW) / 2
          const oy = heightProp - 10 + rowIdx * 60
          return (
            <g key={`extra-${rowIdx}`} transform={`translate(${ox}, ${oy})`}>
              <text
                x={-8}
                y={CELL_SIZE / 2}
                textAnchor="end"
                dominantBaseline="central"
                fontSize={11}
                fill="var(--color-muted-foreground)"
              >
                {extra.label}
              </text>
              {extra.values.map((val, i) => (
                <g key={i}>
                  <rect
                    x={i * cellW}
                    y={0}
                    width={CELL_SIZE}
                    height={CELL_SIZE}
                    rx={4}
                    fill="var(--color-muted)"
                    stroke="var(--color-border)"
                    strokeWidth={1}
                  />
                  <text
                    x={i * cellW + CELL_SIZE / 2}
                    y={CELL_SIZE / 2}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={11}
                    fontFamily="ui-monospace, monospace"
                    fill="var(--color-foreground)"
                  >
                    {val}
                  </text>
                </g>
              ))}
            </g>
          )
        })}
      </svg>

      <PlaybackControls
        message={currentMessage}
        isPlaying={isPlaying}
        atStart={atStart}
        atEnd={atEnd}
        stepIndex={stepIndex}
        totalSteps={steps.length}
        onStepBack={stepBack}
        onStepForward={stepForward}
        onPlay={play}
        onPause={pause}
        onReset={reset}
      />
    </div>
  )
}
